import type { ButtonHTMLAttributes, HTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { AlertCircle, Check, Copy, Inbox, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { PointsStatus, TaskStatus } from "../lib/types";

type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: "default" | "sm";
}

export function Button({ variant = "secondary", size = "default", className = "", ...props }: ButtonProps) {
  return <button className={`button button-${variant} ${size === "sm" ? "button-sm" : ""} ${className}`} {...props} />;
}

export function IconButton({ title, className = "", ...props }: ButtonProps & { title: string }) {
  return <Button aria-label={title} title={title} className={`button-icon ${className}`} {...props} />;
}

type FieldProps = {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
  className?: string;
};

export function Field({ label, hint, error, children, className = "" }: FieldProps) {
  return (
    <label className={`field ${className}`}>
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
      {error && <span className="field-error">{error}</span>}
    </label>
  );
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={`input ${props.className ?? ""}`} {...props} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={`input ${props.className ?? ""}`} {...props} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={`input ${props.className ?? ""}`} {...props} />;
}

export function Segmented<T extends string>({ value, options, onChange, ariaLabel }: { value: T; options: Array<{ value: T; label: string }>; onChange: (value: T) => void; ariaLabel: string }) {
  return (
    <div className="segmented" role="group" aria-label={ariaLabel}>
      {options.map((option) => (
        <button key={option.value} type="button" className="segment" aria-pressed={value === option.value} onClick={() => onChange(option.value)}>
          {option.label}
        </button>
      ))}
    </div>
  );
}

const statusMeta: Record<TaskStatus, { label: string; className: string }> = {
  queued: { label: "排队中", className: "status-neutral" },
  preparing: { label: "准备中", className: "status-running" },
  submitted: { label: "已提交", className: "status-running" },
  in_progress: { label: "生成中", className: "status-running" },
  storing: { label: "转存中", className: "status-warning" },
  completed: { label: "已完成", className: "status-success" },
  failed: { label: "失败", className: "status-danger" },
  timed_out: { label: "已超时", className: "status-danger" }
};

export function StatusBadge({ status }: { status: TaskStatus }) {
  const meta = statusMeta[status] ?? { label: status, className: "status-neutral" };
  return <span className={`status-badge ${meta.className}`}>{meta.label}</span>;
}

const pointsStatusMeta: Record<PointsStatus, { label: string; className: string }> = {
  not_applicable: { label: "不适用", className: "status-neutral" },
  reserved: { label: "已预占", className: "status-warning" },
  charged: { label: "已扣除", className: "status-success" },
  released: { label: "已退回", className: "status-neutral" }
};

export function PointsStatusBadge({ status }: { status?: PointsStatus }) {
  const meta = pointsStatusMeta[status ?? "not_applicable"];
  return <span className={`status-badge ${meta.className}`}>{meta.label}</span>;
}

export function BoolBadge({ value, trueLabel = "正常", falseLabel = "异常" }: { value: boolean; trueLabel?: string; falseLabel?: string }) {
  return <span className={`status-badge ${value ? "status-success" : "status-danger"}`}>{value ? trueLabel : falseLabel}</span>;
}

export function PageHeader({ title, description, actions }: { title: string; description?: string; actions?: ReactNode }) {
  return (
    <header className="page-header">
      <div>
        <h1 className="page-title">{title}</h1>
        {description && <p className="page-description">{description}</p>}
      </div>
      {actions && <div className="toolbar">{actions}</div>}
    </header>
  );
}

export function Panel({ children, className = "", ...props }: HTMLAttributes<HTMLDivElement>) {
  return <section className={`panel ${className}`} {...props}>{children}</section>;
}

export function PanelHeader({ title, actions }: { title: ReactNode; actions?: ReactNode }) {
  return <div className="panel-header"><h2 className="panel-title">{title}</h2>{actions}</div>;
}

export function EmptyState({ title, description, action, icon }: { title: string; description: string; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="empty-state">
      <div>
        <div className="empty-state-icon">{icon ?? <Inbox size={19} strokeWidth={1.8} />}</div>
        <h3>{title}</h3>
        <p>{description}</p>
        {action}
      </div>
    </div>
  );
}

export function InlineError({ message, action }: { message: string; action?: ReactNode }) {
  return <div className="inline-error" role="alert"><AlertCircle size={16} strokeWidth={1.8} /><div className="min-w-0 flex-1">{message}</div>{action}</div>;
}

export function SkeletonRows({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="p-3" aria-label="正在加载">
      {Array.from({ length: rows }).map((_, row) => (
        <div key={row} className="grid h-12 items-center gap-4 border-b border-[var(--border)] last:border-0" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
          {Array.from({ length: columns }).map((__, col) => <div key={col} className="skeleton h-3" style={{ width: `${58 + ((row + col) % 4) * 10}%` }} />)}
        </div>
      ))}
    </div>
  );
}

export function CopyButton({ value, label = "复制" }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };
  return <Button type="button" size="sm" onClick={copy}>{copied ? <Check size={14} /> : <Copy size={14} />}{copied ? "已复制" : label}</Button>;
}

export function Modal({ title, children, footer, onClose }: { title: string; children: ReactNode; footer?: ReactNode; onClose: () => void }) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();
    const handleKeyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCloseRef.current();
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'));
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyboard);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyboard);
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
    };
  }, []);

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div ref={dialogRef} className="modal" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}>
        <div className="modal-header"><strong>{title}</strong><IconButton variant="ghost" title="关闭" onClick={onClose}><X size={16} /></IconButton></div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

export function formatDate(value?: string | null) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).format(date);
}

export function formatMoney(value?: string | number | null) {
  if (value === undefined || value === null || value === "") return "-";
  return `$${Number(value).toFixed(6)}`;
}

export function formatPoints(value?: string | number | null) {
  if (value === undefined || value === null || value === "") return "-";
  return `${Number(value).toFixed(6)} 积分`;
}
